import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const BANNER_IMAGE =
  'https://roadtechservices.netlify.app/images/hero_img-1920w.jpeg';
const CTA_IMAGE =
  'https://roadtechservices.netlify.app/images/mower_banner-850w.jpeg';

const services = [
  {
    title: 'Mobile Lawn Mower Repair',
    description:
      'Expert repair and maintenance for push and riding mowers — all done right at your doorstep. No hauling, no dealer wait. We service all major brands and fix everything from engine issues to blade replacements.',
    href: '/services/lawn-mower',
    image:
      'https://roadtechservices.netlify.app/images/mobilelawn/mower-repair-1.jpg',
    items: [
      'Push & riding mower repair',
      'Blade sharpening & replacement',
      'Oil changes & tune-ups',
      'Carburetor cleaning & adjustment',
      'Belt & drive system repairs',
    ],
    tag: 'Lawn Mowers',
  },
  {
    title: 'Snow Blower Repair & Maintenance',
    description:
      'Stay ready for Michigan winters with certified snow blower service. Whether it\'s a clogged auger, starting issues, or a pre-season tune-up — we handle it at your home before the snow hits.',
    href: '/services/snow-blower',
    image:
      'https://roadtechservices.netlify.app/images/snowblower_0.jpg',
    items: [
      'Single & two-stage snow blowers',
      'Seasonal tune-ups & inspections',
      'Belt, spark plug & carburetor repair',
      'Auger & chute repairs',
      'Starting issues & ignition work',
    ],
    tag: 'Snow Blowers',
  },
  {
    title: 'Generator Repair & Service',
    description:
      'Never be left without power. Our certified technician services portable and standby generators on-site, so your backup power is ready when storms hit and you need it most.',
    href: '/services/generator',
    image:
      'https://roadtechservices.netlify.app/images/generators/generator-repair-1.jpg',
    items: [
      'Portable & standby generators',
      'Engine diagnostics & troubleshooting',
      'Oil changes & filter replacements',
      'Carburetor cleaning & adjustment',
      'Starter & electrical repairs',
    ],
    tag: 'Generators',
  },
];

export default function Services() {
  return (
    <div className="font-body">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 min-h-[420px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={BANNER_IMAGE}
            alt="Road Tech Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 text-center px-6">
          <p className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-3">
            What We Offer
          </p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight">
            Our Services
          </h1>
        </div>
      </section>

      {/* Intro strip */}
      <div className="bg-orange-600 py-5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap justify-center gap-8">
          {[
            { icon: 'ri-home-gear-line', text: 'We Come to You' },
            { icon: 'ri-time-line', text: 'Same-Day Service Available' },
            { icon: 'ri-shield-star-line', text: 'Multi-Brand Certified' },
            { icon: 'ri-tools-line', text: 'All Major Brands Serviced' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-white text-sm font-medium">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-white/80`} />
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Services */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="space-y-24">
            {services.map((service, idx) => (
              <div
                key={service.href}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center`}
              >
                {/* Image — alternates sides */}
                <div
                  className={`relative h-[420px] rounded-2xl overflow-hidden ${
                    idx % 2 === 1 ? 'lg:order-2' : ''
                  }`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                      {service.tag}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900 mb-4 leading-tight">
                    {service.title}
                  </h2>
                  <p className="text-neutral-600 text-base leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="bg-stone-50 rounded-xl p-6 mb-8">
                    <p className="text-neutral-900 font-semibold text-xs mb-4 uppercase tracking-wider">
                      What We Cover
                    </p>
                    <ul className="space-y-3">
                      {service.items.map((s) => (
                        <li
                          key={s}
                          className="flex items-center gap-3 text-sm text-neutral-600"
                        >
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-base" />
                          </div>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={service.href}
                    className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    Learn More
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0">
          <img
            src={CTA_IMAGE}
            alt="Mobile repair"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-5">
            Ready to Schedule Service?
          </h2>
          <p className="text-white/75 text-base leading-relaxed mb-8">
            Contact Road Tech Services today — we come to you. Same-day service
            when possible.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            Book a Service
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
